// @flow

import React, { useState, useCallback, useMemo, forwardRef } from 'react'
import styled from 'styled-components'
import { MEDIUM } from '@streamr/streamr-layout/styled'
import Timeframe from './Timeframe'
import LoadingIndicator from '~shared/LoadingIndicator'

type Props = {
    className?: string,
    style: any,
    switchTimeframe: (string) => void,
    loading?: boolean,
}

const timeframes = [
    Timeframe.TODAY,
    Timeframe.SEVEN_DAYS,
    Timeframe.ONE_MONTH,
    Timeframe.ONE_YEAR,
    Timeframe.ALL,
]

const Links = styled.ul`
    align-items: center;
    box-sizing: border-box;
    display: flex;
    height: 100%;
    list-style: none;
    margin: 0.75rem 0;
    overflow: visible;
    padding: 0;
    transition: none;
    width: 100%;

    > li {
        height: 100%;
        width: ${(100 / Math.max(1, timeframes.length))}%;
        padding: 0;
    }
`

const TabButton = styled.button`
    appearance: none;
    background-color: transparent;
    border: 0;
    cursor: pointer;
    display: block;
    font-size: 0.875rem;
    height: 100%;
    letter-spacing: 2px;
    line-height: 1em;
    outline: none !important;
    padding: 0;
    text-transform: uppercase;
    width: 100%;
    text-align: center;
    color: ${({ active }) => (active ? '#0C009A' : '#CDCDCD')};
    transition: color 180ms ease-in-out, opacity 180ms ease-in-out;

    :not([disabled]):hover {
        color: #0C009A;
    }

    &[disabled] {
        opacity: 0.5;
        cursor: auto;
    }
`

const TrackMask = styled.div`
    margin: 0 12px;
    overflow: hidden;
    height: 1px;
`

const UnderlineTrack = styled.div`
    position: relative;
    height: 1px;
    background-color: #CDCDCD;
    margin: 0 -12px;

    > * {
        position: absolute;
    }
`

const MovingMarker = styled.div`
    position: absolute;
    transition: all 300ms ease-in-out;
    display: flex;
    justify-content: center;
    position: absolute;
    width: 20%;
    width: ${(100 / Math.max(1, timeframes.length))}%;
    opacity: 0;
`

const Marker = styled.div`
    width: calc(100% - 24px);
    height: 1px;
    background-color: #0C009A;
`

const UnstyledNav = forwardRef(({ className, switchTimeframe, style, loading }: Props, ref: any) => {
    const [activeNav, setActiveNav] = useState(Timeframe.TODAY)

    const updatePriceChart = useCallback((timePeriod) => {
        switchTimeframe(timePeriod)
        setActiveNav(timePeriod)
    }, [switchTimeframe])

    const activeIndex = useMemo(() => Math.max(0, timeframes.indexOf(activeNav)), [activeNav])

    return (
        <div className={className} ref={ref} style={style}>
            <Links>
                {timeframes.map((timeframe) => (
                    <li key={timeframe}>
                        <TabButton
                            active={activeNav === timeframe}
                            onClick={() => updatePriceChart(timeframe)}
                            type="button"
                            disabled={!!loading}
                        >
                            {timeframe}
                        </TabButton>
                    </li>
                ))}
            </Links>
            <TrackMask>
                <UnderlineTrack>
                    <LoadingIndicator loading={loading} />
                    <MovingMarker style={{
                        left: `${activeIndex * (100 / Math.max(1, timeframes.length))}%`,
                        opacity: !loading ? 1 : 0,
                    }}
                    >
                        <Marker />
                    </MovingMarker>
                </UnderlineTrack>
            </TrackMask>
        </div>
    )
})

const Nav = styled(UnstyledNav)`
    font-size: 0.75rem;
    font-weight: ${MEDIUM};
    line-height: 1rem;
    padding: 12px 28px 22px 20px;
    background: #F8F8F8;
    border-radius: 8px;
`

export default Object.assign(Nav, {
    Timeframe,
})
